import qs from 'qs';
import { useMemo } from 'react';

import { useQuery } from '../../../contexts/RouterContext';
import { STANDARD_REPORT_OPTIONS } from '../constants';
import type { ReportsV3QueryParams, ReportV3Format } from '../types';

import { useReportFlow } from './useReportFlow';

/**
 * Returns format-specific include params for the standard report flow.
 */
const getFormatSpecificParams = (format?: ReportV3Format): Record<string, boolean> => {
  if (!format) {
    return {};
  }
  switch (format) {
    case 'TXT':
      return { includeFileMatches: true };
    case 'CYCLONEDX_JSON':
    case 'CYCLONEDX_XML':
      return { includeClosedVulnerabilities: true, includeSupportStatus: true };
    case 'HTML':
    case 'PDF':
    case 'CSV':
    case 'MD':
    case 'SPDX':
    case 'SPDX_JSON':
    case 'JSON':
      return {};
    default: {
      // If we get here, a new format was added to ReportV3Format
      // but not handled here. Add a case for it above.
      const exhaustiveCheck: never = format;
      return exhaustiveCheck;
    }
  }
};

/**
 * Returns a query string of report option params based on the current report
 * flow. For the standard flow this is a fixed set of include options plus
 * package metadata fields and format-specific params. For the custom flow it
 * returns an empty string (placeholder for future work).
 */
export const useReportOptionsParams = (): string => {
  const [flow] = useReportFlow();
  const [query] = useQuery<ReportsV3QueryParams>();

  return useMemo(() => {
    if (flow === 'custom') {
      return '';
    }

    const includeParams = STANDARD_REPORT_OPTIONS
      .filter((o): o is Extract<typeof o, { kind: 'includeBoolean' }> => o.kind === 'includeBoolean')
      .reduce<Record<string, boolean>>((acc, o) => {
        acc[o.id] = true;
        return acc;
      }, {});

    const dependencyInfoOptions = STANDARD_REPORT_OPTIONS
      .filter((o): o is Extract<typeof o, { kind: 'dependencyInfoGroup' }> => o.kind === 'dependencyInfoGroup')
      .flatMap((o) => o.dependencyInfoOptions);

    const params: Record<string, unknown> = {
      ...includeParams,
      ...(dependencyInfoOptions.length > 0 ? { dependencyInfoOptions } : {}),
      ...getFormatSpecificParams(query.format),
    };

    return qs.stringify(params);
  }, [flow, query.format]);
};
